import { logPrint } from "./my-utils";

/**
 * 批量并发执行异步任务的工具类
 * 
 * @example
 * // 基础用法：批量获取用户信息
 * const userIds = ['user1', 'user2', 'user3'];
 * const userInfos = await BatchProcessorUtils.processBatch(
 *   userIds,
 *   async (userId) => await getUserInfo(userId),
 *   2 // 每次并发2个请求
 * );
 * 
 * @example
 * // 容错用法：批量上传文件（某个失败不影响其他）
 * const files = [file1, file2, file3];
 * const results = await BatchProcessorUtils.processBatchWithFallback(
 *   files,
 *   async (file) => await uploadFile(file),
 *   3
 * );
 * // 处理结果
 * results.forEach((result, index) => {
 *   if (result instanceof Error) {
 *     console.error(`文件 ${index} 上传失败:`, result);
 *   } else {
 *     console.log(`文件 ${index} 上传成功:`, result);
 *   }
 * });
 * 
 * @example
 * // 详细结果用法：需要区分成功和失败的场景
 * const { successes, failures } = await BatchProcessorUtils.processBatchWithResults(
 *   items,
 *   async (item) => await processItem(item),
 *   5
 * );
 * console.log(`成功: ${successes.length}, 失败: ${failures.length}`);
 * successes.forEach(({ index, result }) => console.log(`项目 ${index} 成功:`, result));
 * failures.forEach(({ index, error }) => console.error(`项目 ${index} 失败:`, error));
 */
export class BatchProcessorUtils {
  /**
   * 批量并发处理任务，控制并发数量
   * 严格模式：任何一个任务失败都会导致整批失败
   * 
   * @param items 需要处理的数据数组
   * @param processor 处理单个项目的异步函数
   * @param batchSize 每批并发处理的数量，默认为3
   * @returns Promise<T[]> 处理结果数组
   */
  static async processBatch<T, R>(
    items: T[],
    processor: (item: T, index: number) => Promise<R>,
    batchSize = 3
  ): Promise<R[]> {
    const results: R[] = [];

    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchPromises = batch.map((item, batchIndex) =>
        processor(item, i + batchIndex)
      );

      try {
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      } catch (error) {
        logPrint('批量处理失败:', error);
        throw error;
      }
    }

    return results;
  }

  /**
   * 批量并发处理任务，容错版本（某个任务失败不影响其他任务）
   * 推荐使用：适合大部分业务场景，单个失败不影响整体
   * 
   * @param items 需要处理的数据数组
   * @param processor 处理单个项目的异步函数
   * @param batchSize 每批并发处理的数量，默认为3
   * @returns Promise<(R | Error)[]> 处理结果数组，失败的项目返回Error对象
   */
  static async processBatchWithFallback<T, R>(
    items: T[],
    processor: (item: T, index: number) => Promise<R>,
    batchSize = 3
  ): Promise<(R | Error)[]> {
    const results: (R | Error)[] = [];

    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchPromises = batch.map(async (item, batchIndex) => {
        try {
          return await processor(item, i + batchIndex);
        } catch (error) {
          logPrint(`处理项目 ${i + batchIndex} 失败:`, error);
          return error as Error;
        }
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }

    return results;
  }

  /**
   * 批量并发处理任务，返回成功和失败的分离结果
   * 适用场景：需要详细分析成功和失败情况，或需要根据索引对应原始数据
   * 
   * @param items 需要处理的数据数组
   * @param processor 处理单个项目的异步函数
   * @param batchSize 每批并发处理的数量，默认为3
   * @returns Promise<{successes: {index: number, result: R}[], failures: {index: number, error: Error}[]}>
   */
  static async processBatchWithResults<T, R>(
    items: T[],
    processor: (item: T, index: number) => Promise<R>,
    batchSize = 3
  ): Promise<{
    successes: { index: number; result: R }[];
    failures: { index: number; error: Error }[];
  }> {
    const successes: { index: number; result: R }[] = [];
    const failures: { index: number; error: Error }[] = [];

    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchPromises = batch.map(async (item, batchIndex) => {
        const globalIndex = i + batchIndex;
        try {
          const result = await processor(item, globalIndex);
          return { success: true, index: globalIndex, result };
        } catch (error) {
          logPrint(`处理项目 ${globalIndex} 失败:`, error);
          return { success: false, index: globalIndex, error: error as Error };
        }
      });

      const batchResults = await Promise.all(batchPromises);

      batchResults.forEach((result) => {
        if (result.success) {
          successes.push({ index: result.index, result: result.result as R });
        } else {
          failures.push({ index: result.index, error: result.error as Error });
        }
      });
    }

    return { successes, failures };
  }
}

/**
 * 使用指南和最佳实践
 * 
 * ## 选择合适的方法：
 * 
 * ### 1. processBatch（严格模式）
 * - ✅ 适用于：所有操作必须成功的场景
 * - ✅ 适用于：数据一致性要求高的场景
 * - ❌ 不适用于：容许部分失败的场景
 * - 示例：批量删除操作、事务性操作
 * 
 * ### 2. processBatchWithFallback（推荐）
 * - ✅ 适用于：大部分业务场景
 * - ✅ 适用于：部分失败可接受的场景
 * - ✅ 适用于：需要最大化成功率的场景
 * - 示例：批量获取数据、文件上传、消息发送
 * 
 * ### 3. processBatchWithResults（详细分析）
 * - ✅ 适用于：需要详细分析结果的场景
 * - ✅ 适用于：需要生成报告的场景
 * - ✅ 适用于：失败后需要重试的场景
 * - 示例：数据同步、批量导入、状态统计
 * 
 * ## 并发数设置建议：
 * 
 * - **网络API请求**: 3-5个并发（避免服务器压力过大）
 * - **文件操作**: 2-3个并发（避免I/O阻塞）
 * - **CPU密集型**: 1-2个并发（避免阻塞主线程）
 * - **数据库操作**: 2-4个并发（根据连接池大小调整）
 * 
 * ## 错误处理策略：
 * 
 * ```typescript
 * // 方式1：使用 processBatchWithFallback 简单处理
 * const results = await BatchProcessorUtils.processBatchWithFallback(items, processor);
 * const successful = results.filter(r => !(r instanceof Error));
 * 
 * // 方式2：使用 processBatchWithResults 详细处理
 * const { successes, failures } = await BatchProcessorUtils.processBatchWithResults(items, processor);
 * if (failures.length > 0) {
 *   // 重试失败的项目
 *   const retryItems = failures.map(f => items[f.index]);
 * }
 * ```
 * 
 * ## 性能优化tips：
 * 
 * 1. **合理设置批次大小**：根据网络状况和服务器性能调整
 * 2. **监控处理进度**：在processor函数中添加日志
 * 3. **避免内存泄漏**：处理大量数据时及时清理不需要的变量
 * 4. **错误重试机制**：对于临时性错误，考虑实现重试逻辑
 */

